// Force Unknown Effects Module
// All chaos effects that can be applied to pieces and lines

import { CANVAS_W, CANVAS_H, dist } from './patternAnalysis';

const PIECE_R = 22;

// --- Normal Effects (always available) ---

export function effectScatter(pieces, difficulty = 0) {
  const n = 2 + Math.floor(Math.random() * 3) + Math.floor(difficulty * 2);
  const nonRemnant = pieces.filter(p => !p.isRemnant && p.type !== 'chaos');
  const shuffled = [...nonRemnant].sort(() => Math.random() - 0.5);
  const toMove = shuffled.slice(0, Math.min(n, shuffled.length));
  const ids = new Set(toMove.map(p => p.id));
  return pieces.map(p => ids.has(p.id) ? {
    ...p,
    x: 40 + Math.random() * (CANVAS_W - 80),
    y: 40 + Math.random() * (CANVAS_H - 80),
  } : p);
}

export function effectVoidBurst(pieces, difficulty = 0) {
  const cx = 80 + Math.random() * (CANVAS_W - 160);
  const cy = 80 + Math.random() * (CANVAS_H - 160);
  const radius = 80 + Math.random() * 60 + difficulty * 30;
  return pieces.filter(p => p.isRemnant || dist(p, { x: cx, y: cy }) > radius);
}

export function effectWarp(pieces, difficulty = 0) {
  const wx = Math.random() * CANVAS_W;
  const wy = Math.random() * CANVAS_H;
  const strength = 25 + Math.random() * 40 + difficulty * 20;
  const pull = Math.random() > 0.5;
  return pieces.map(p => {
    if (p.isRemnant) return p;
    const d = dist(p, { x: wx, y: wy });
    if (d < 5) return p;
    const dx = (wx - p.x) / d;
    const dy = (wy - p.y) / d;
    const force = pull ? strength : -strength;
    return {
      ...p,
      x: Math.max(PIECE_R, Math.min(CANVAS_W - PIECE_R, p.x + dx * force)),
      y: Math.max(PIECE_R, Math.min(CANVAS_H - PIECE_R, p.y + dy * force)),
    };
  });
}

export function effectShuffle(pieces, difficulty = 0) {
  const movable = pieces.filter(p => !p.isRemnant && p.type !== 'chaos');
  if (movable.length < 2) return pieces;
  const pairs = Math.min(3 + Math.floor(difficulty * 2), Math.floor(movable.length / 2));
  const shuffled = [...movable].sort(() => Math.random() - 0.5);
  const swaps = new Map();
  for (let i = 0; i < pairs * 2; i += 2) {
    swaps.set(shuffled[i].id, { x: shuffled[i + 1].x, y: shuffled[i + 1].y });
    swaps.set(shuffled[i + 1].id, { x: shuffled[i].x, y: shuffled[i].y });
  }
  return pieces.map(p => swaps.has(p.id) ? { ...p, ...swaps.get(p.id) } : p);
}

export function effectInject(pieces, difficulty = 0) {
  const n = 2 + Math.floor(Math.random() * 3) + Math.floor(difficulty * 2);
  const newPieces = [...pieces];
  for (let i = 0; i < n; i++) {
    newPieces.push({
      id: `chaos_${Date.now()}_${i}`,
      type: 'chaos',
      x: 40 + Math.random() * (CANVAS_W - 80),
      y: 40 + Math.random() * (CANVAS_H - 80),
      placedAt: Date.now(),
      isRemnant: false,
    });
  }
  return newPieces;
}

// --- Radical Effects (triggered sometimes, more likely at higher difficulty) ---

export function effectRadicalClear(pieces, difficulty = 0) {
  const nonRemnant = pieces.filter(p => !p.isRemnant && p.type !== 'chaos');
  const clearPercent = 0.3 + Math.random() * 0.3 + difficulty * 0.1;
  const toRemove = Math.floor(nonRemnant.length * clearPercent);
  const shuffled = [...nonRemnant].sort(() => Math.random() - 0.5);
  const removeIds = new Set(shuffled.slice(0, toRemove).map(p => p.id));
  return pieces.filter(p => !removeIds.has(p.id));
}

export function effectRadicalExplode(pieces, difficulty = 0) {
  const cx = CANVAS_W / 2;
  const cy = CANVAS_H / 2;
  const force = 60 + Math.random() * 40 + difficulty * 30;
  return pieces.map(p => {
    if (p.isRemnant) return p;
    const d = dist(p, { x: cx, y: cy });
    if (d < 5) return { ...p, x: Math.random() * CANVAS_W, y: Math.random() * CANVAS_H };
    const dx = (p.x - cx) / d;
    const dy = (p.y - cy) / d;
    return {
      ...p,
      x: Math.max(PIECE_R, Math.min(CANVAS_W - PIECE_R, p.x + dx * force)),
      y: Math.max(PIECE_R, Math.min(CANVAS_H - PIECE_R, p.y + dy * force)),
    };
  });
}

export function effectRadicalRotate(pieces, difficulty = 0) {
  const cx = CANVAS_W / 2;
  const cy = CANVAS_H / 2;
  const angle = (45 + Math.random() * 90) * Math.PI / 180;
  return pieces.map(p => {
    if (p.isRemnant) return p;
    const dx = p.x - cx;
    const dy = p.y - cy;
    const newX = cx + dx * Math.cos(angle) - dy * Math.sin(angle);
    const newY = cy + dx * Math.sin(angle) + dy * Math.cos(angle);
    return {
      ...p,
      x: Math.max(PIECE_R, Math.min(CANVAS_W - PIECE_R, newX)),
      y: Math.max(PIECE_R, Math.min(CANVAS_H - PIECE_R, newY)),
    };
  });
}

export function effectRadicalMirror(pieces, difficulty = 0) {
  const horizontal = Math.random() > 0.5;
  return pieces.map(p => {
    if (p.isRemnant) return p;
    if (horizontal) {
      return { ...p, x: CANVAS_W - p.x };
    } else {
      return { ...p, y: CANVAS_H - p.y };
    }
  });
}

export function effectRadicalCompress(pieces, difficulty = 0) {
  const cx = CANVAS_W / 2;
  const cy = CANVAS_H / 2;
  const factor = 0.3 + Math.random() * 0.3;
  return pieces.map(p => {
    if (p.isRemnant) return p;
    const newX = cx + (p.x - cx) * factor;
    const newY = cy + (p.y - cy) * factor;
    return {
      ...p,
      x: Math.max(PIECE_R, Math.min(CANVAS_W - PIECE_R, newX)),
      y: Math.max(PIECE_R, Math.min(CANVAS_H - PIECE_R, newY)),
    };
  });
}

// Effect arrays for random selection
export const CHAOS_EFFECTS = [effectScatter, effectVoidBurst, effectWarp, effectShuffle, effectInject];
export const CHAOS_NAMES = ['Scatter', 'Void Burst', 'Gravity Warp', 'Shuffle', 'Chaos Inject'];

export const RADICAL_EFFECTS = [effectRadicalClear, effectRadicalExplode, effectRadicalRotate, effectRadicalMirror, effectRadicalCompress];
export const RADICAL_NAMES = ['Mass Clear', 'Explosion', 'Rotation', 'Mirror', 'Compression'];

// Line effects for Force Unknown
export function applyLineEffects(lines, difficulty = 0, isRadical = false) {
  if (isRadical) {
    // Radical effects - more destructive to lines
    const lineDestroyChance = 0.3 + difficulty * 0.2;
    return lines.filter(() => Math.random() > lineDestroyChance);
  }
  
  // Normal varied line effects
  const availableColors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];
  
  return lines.map(line => {
    const effectType = Math.random();
    
    if (effectType < 0.15) {
      // 15% chance: Erase part of the line
      const startKeep = Math.floor(Math.random() * line.points.length * 0.3);
      const endKeep = Math.floor(line.points.length * 0.5 + Math.random() * line.points.length * 0.5);
      return { ...line, points: line.points.slice(startKeep, endKeep) };
    } else if (effectType < 0.30) {
      // 15% chance: Change color
      const newColor = availableColors[Math.floor(Math.random() * availableColors.length)];
      return { ...line, color: newColor };
    } else if (effectType < 0.50) {
      // 20% chance: Sine wave distortion
      const amplitude = 10 + Math.random() * 20;
      const frequency = 0.05 + Math.random() * 0.1;
      const phase = Math.random() * Math.PI * 2;
      return {
        ...line,
        points: line.points.map((p, i) => ({
          x: p.x + Math.sin(i * frequency + phase) * amplitude,
          y: p.y + Math.cos(i * frequency + phase) * amplitude * 0.5,
        })),
      };
    } else if (effectType < 0.70) {
      // 20% chance: Partial distortion
      const distortStart = Math.floor(Math.random() * line.points.length * 0.5);
      const distortEnd = distortStart + Math.floor(Math.random() * line.points.length * 0.5);
      const distortStrength = 20 + difficulty * 15;
      return {
        ...line,
        points: line.points.map((p, i) => {
          if (i >= distortStart && i <= distortEnd) {
            return {
              x: p.x + (Math.random() - 0.5) * distortStrength,
              y: p.y + (Math.random() - 0.5) * distortStrength,
            };
          }
          return p;
        }),
      };
    } else if (effectType < 0.85) {
      // 15% chance: Full distortion
      const distortStrength = 25 + difficulty * 15;
      return {
        ...line,
        points: line.points.map(p => ({
          x: p.x + (Math.random() - 0.5) * distortStrength,
          y: p.y + (Math.random() - 0.5) * distortStrength,
        })),
      };
    } else {
      // 15% chance: No effect
      return line;
    }
  }).filter(line => line.points.length >= 2);
}
